/**
 * BottomControlBar Component
 *
 * Floating control bar for tree navigation and actions.
 * Provides zoom controls, center view, and export functionality.
 *
 * Responsive behavior:
 * - Mobile (<640px): Minimal controls, hide zoom on touch devices
 * - Tablet (640px-1024px): Show all controls with medium sizing
 * - Desktop (>1024px): Full controls with labels and larger sizing
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTouchDevice } from '@/hooks/shared/useTouchDevice';

interface BottomControlBarProps {
  zoom: number; // Current zoom level (0.5 - 2.0)
  onZoomIn: () => void;
  onZoomOut: () => void;
  onCenter: () => void;
  onExport?: () => void;
  onPan?: (direction: 'up' | 'down' | 'left' | 'right') => void; // For directional pan controls
  minZoom?: number;
  maxZoom?: number;
}

export function BottomControlBar({
  zoom,
  onZoomIn,
  onZoomOut,
  onCenter,
  onExport,
  onPan,
  minZoom = 0.5,
  maxZoom = 2.0,
}: BottomControlBarProps) {
  const { t } = useTranslation('common');
  const isTouchDevice = useTouchDevice();

  // Track window width for responsive behavior
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Convert zoom scale to percentage (1.0 = 100%)
  const zoomPercentage = Math.round(zoom * 100);

  // Check if at min/max zoom
  const isMinZoom = zoom <= minZoom;
  const isMaxZoom = zoom >= maxZoom;

  // Determine screen size breakpoints
  const isMobile = windowWidth < 640;
  const isTablet = windowWidth >= 640 && windowWidth < 1024;
  const isDesktop = windowWidth >= 1024;

  // Desktop (>=1024px): Show zoom controls
  // Mobile/Tablet (<1024px): Pan controls removed - users can pan via touch gestures
  const showZoomControls = windowWidth >= 1024;
  const showDirectionalPan = false; // Removed per user request for mobile screens

  return (
    <>
      {/* Mobile/Tablet: Directional Pan Controls on LEFT Side (<1024px) */}
      {showDirectionalPan && (
        <div className="absolute left-4 md:left-6 bottom-24 md:bottom-28 flex flex-col gap-2 z-40">
          {/* Up */}
          <button
            onClick={() => onPan?.('up')}
            className="size-10 md:size-11 rounded-full bg-white/95 dark:bg-slate-900/95 border border-slate-200 dark:border-slate-800 backdrop-blur-sm shadow-lg hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center transition-colors active:scale-95"
            aria-label="Pan up"
            title="Pan up"
          >
            <span className="material-symbols-outlined text-lg md:text-xl text-slate-700 dark:text-slate-300">arrow_upward</span>
          </button>

          {/* Down */}
          <button
            onClick={() => onPan?.('down')}
            className="size-10 md:size-11 rounded-full bg-white/95 dark:bg-slate-900/95 border border-slate-200 dark:border-slate-800 backdrop-blur-sm shadow-lg hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center transition-colors active:scale-95"
            aria-label="Pan down"
            title="Pan down"
          >
            <span className="material-symbols-outlined text-lg md:text-xl text-slate-700 dark:text-slate-300">arrow_downward</span>
          </button>

          {/* Left */}
          <button
            onClick={() => onPan?.('left')}
            className="size-10 md:size-11 rounded-full bg-white/95 dark:bg-slate-900/95 border border-slate-200 dark:border-slate-800 backdrop-blur-sm shadow-lg hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center transition-colors active:scale-95"
            aria-label="Pan left"
            title="Pan left"
          >
            <span className="material-symbols-outlined text-lg md:text-xl text-slate-700 dark:text-slate-300">arrow_back</span>
          </button>

          {/* Right */}
          <button
            onClick={() => onPan?.('right')}
            className="size-10 md:size-11 rounded-full bg-white/95 dark:bg-slate-900/95 border border-slate-200 dark:border-slate-800 backdrop-blur-sm shadow-lg hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center transition-colors active:scale-95"
            aria-label="Pan right"
            title="Pan right"
          >
            <span className="material-symbols-outlined text-lg md:text-xl text-slate-700 dark:text-slate-300">arrow_forward</span>
          </button>
        </div>
      )}

      {/* Bottom Center Bar */}
      <div className="absolute bottom-3 md:bottom-4 lg:bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-0.5 md:gap-1 lg:gap-2 p-1 md:p-1.5 lg:p-2 bg-white/95 dark:bg-slate-900/95 border border-slate-200 dark:border-slate-800 backdrop-blur-sm rounded-full shadow-xl z-40">

        {/* Zoom Controls - Desktop only (>=1024px) */}
        {showZoomControls && (
          <>
            {/* Zoom Out */}
            <button
              onClick={onZoomOut}
              disabled={isMinZoom}
              className="size-8 md:size-9 lg:size-10 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
              aria-label={t('tree.controlBar.zoomOut')}
              title={t('tree.controlBar.zoomOut')}
            >
              <span className="material-symbols-outlined text-base md:text-lg lg:text-xl">zoom_out</span>
            </button>

            {/* Zoom Level */}
            <div className="px-1.5 md:px-2 lg:px-3 text-[10px] md:text-xs lg:text-sm font-semibold text-slate-600 dark:text-slate-400 min-w-[2.5rem] md:min-w-[3rem] text-center">
              {zoomPercentage}%
            </div>

            {/* Zoom In */}
            <button
              onClick={onZoomIn}
              disabled={isMaxZoom}
              className="size-8 md:size-9 lg:size-10 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
              aria-label={t('tree.controlBar.zoomIn')}
              title={t('tree.controlBar.zoomIn')}
            >
              <span className="material-symbols-outlined text-base md:text-lg lg:text-xl">zoom_in</span>
            </button>

            {/* Divider */}
            <div className="h-5 md:h-6 w-px bg-slate-200 dark:bg-slate-700 mx-0.5 md:mx-1"></div>
          </>
        )}

        {/* Center View - Always visible */}
        <button
          onClick={onCenter}
          className="size-8 md:size-9 lg:size-10 rounded-full bg-primary/10 text-primary flex items-center justify-center transition-colors hover:bg-primary/20 active:scale-95"
          title={t('tree.controlBar.centerView')}
          aria-label={t('tree.controlBar.centerView')}
        >
          <span className="material-symbols-outlined text-base md:text-lg lg:text-xl">my_location</span>
        </button>

        {/* Export Button - Icon only on mobile/tablet, with text on desktop */}
        {onExport && (
          <>
            <div className="h-5 md:h-6 w-px bg-slate-200 dark:bg-slate-700 mx-0.5 md:mx-1"></div>
            <button
              onClick={onExport}
              className="flex items-center justify-center gap-1.5 md:gap-2 h-8 md:h-9 lg:h-10 px-2.5 md:px-3 lg:px-4 rounded-full bg-primary text-white text-[10px] md:text-xs lg:text-sm font-semibold shadow-lg shadow-primary/20 hover:bg-primary/90 transition-colors active:scale-95"
              aria-label={t('tree.controlBar.export')}
            >
              <span className="material-symbols-outlined text-base md:text-lg lg:text-xl">download</span>
              {/* Show text only on desktop */}
              <span className="hidden lg:inline">
                {t('tree.controlBar.export')}
              </span>
            </button>
          </>
        )}
      </div>
    </>
  );
}
